import { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Center, Flex, Spinner, Text } from '@chakra-ui/react';
import {
  TableOptions,
  DataTable,
  Pagination,
  StatusBadge,
  Button,
  type Column,
  type StatusType,
  type ToggleOption,
} from '@themes/aegix';
import {
  useEventStudents,
  useBulkAccountStudents,
} from '../../../hooks/queries/useReunification';
import { useFeature } from '../../../hooks/useFeature';
import { FeatureCode } from '../../../types/features';
import { toaster } from '../../ui/toaster';
import type { StudentReunification } from '../../../types/ReunificationTypes';
import { StudentDetailDrawer } from './StudentDetailDrawer';
import { AddEventStudentDialog } from '../../reunification/AddEventStudentDialog';

const toggleOptions: ToggleOption[] = [
  { value: 'all', label: 'All' },
  { value: 'unaccounted', label: 'Unaccounted' },
  { value: 'accounted', label: 'Accounted' },
  { value: 'reunified', label: 'Reunified' },
];

interface StudentRow {
  [key: string]: unknown;
  id: string;
  name: string;
  grade: string;
  homeroom: string;
  status: StatusType;
  status_label: string;
  cleared: string;
  raw_status: string;
}

function mapStudentStatus(status: string): StatusType {
  if (['reunified', 'exited', 'released'].includes(status)) return 'active';
  if (['missing', 'unaccounted', 'medical'].includes(status)) return 'inactive';
  return 'pending';
}

const columns: Column<StudentRow>[] = [
  { key: 'name', label: 'Student Name', sortable: true, width: '180px' },
  { key: 'grade', label: 'Grade', sortable: true, width: '80px' },
  { key: 'homeroom', label: 'Homeroom', sortable: true, width: '120px' },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    width: '120px',
    render: (row) => <StatusBadge status={row.status} label={row.status_label} />,
  },
  { key: 'cleared', label: 'Cleared', sortable: false, width: '80px' },
];

function transformStudents(students: StudentReunification[]): StudentRow[] {
  return students.map((s) => ({
    id: s.public_id,
    name: s.student_name,
    grade: s.grade || '---',
    homeroom: s.homeroom || '---',
    status: mapStudentStatus(s.status),
    status_label: s.status_display,
    cleared: s.is_cleared ? 'Yes' : 'No',
    raw_status: s.status,
  }));
}

// Collect the RosterPerson PKs already on the event so the
// picker can exclude them. Falls back to an empty list before the
// first response lands, which means the operator can briefly see a
// not-yet-filtered list — same race window the staff panel has, and
// the unique-together constraint on (event, roster_person) is the
// load-bearing dedupe regardless.
function collectExistingRosterPersonIds(
  students: StudentReunification[] | undefined,
): number[] {
  if (!students) return [];
  const ids = students
    .map((s) => s.roster_person)
    .filter((id): id is number => Number.isFinite(id));
  return Array.from(new Set(ids));
}

interface EventStudentsPanelProps {
  eventId: string;
}

import { useTranslation } from 'react-i18next';

export function EventStudentsPanel({ eventId }: EventStudentsPanelProps) {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [sortColumn, setSortColumn] = useState<string>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<StudentRow>();
  // Add-student dialog state. Mirrors the EventStaffPanel toggle pattern.
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Surface the Add button only when the reunification
  // feature is enabled for this site. Backend
  // `CanOperateStudentCheckIn` is the load-bearing gate and will
  // 403 a direct POST regardless of this flag. A future
  // `REUNIFICATION_MANAGE_STUDENTS` feature flag could give districts
  // finer control without disabling the whole reunification module.
  const canManageStudents = useFeature(FeatureCode.REUNIFICATION);

  const { data, isLoading, isFetching, error } = useEventStudents(eventId, {
    search: searchValue || undefined,
    status: statusFilter === 'all' ? undefined : (statusFilter as 'unaccounted' | 'accounted' | 'reunified'),
    page: currentPage,
    page_size: pageSize,
    ordering: sortColumn
      ? `${sortDirection === 'desc' ? '-' : ''}${sortColumn}`
      : undefined,
  });

  const bulkAccountMutation = useBulkAccountStudents(eventId);

  const students = data?.results ? transformStudents(data.results) : [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const existingRosterPersonIds = useMemo(
    () => collectExistingRosterPersonIds(data?.results),
    [data?.results],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, statusFilter, pageSize]);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((prev) =>
          prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc',
        );
        if (sortDirection === 'desc') setSortColumn(undefined);
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn, sortDirection],
  );

  const handleRowClick = useCallback((row: StudentRow) => {
    setSelectedStudent(row);
  }, []);

  const handleBulkAccount = () => {
    bulkAccountMutation.mutate(
      {},
      {
        onSuccess: (result) => {
          toaster.create({
            title: 'Students accounted',
            description: `${result.updated_count} students marked as accounted`,
            type: 'success',
          });
        },
        onError: (err) => {
          toaster.create({
            title: 'Failed to bulk account',
            description: err.message,
            type: 'error',
          });
        },
      },
    );
  };

  return (
    <>
      <Flex align="center" gap={3} pb={2}>
        <Box flex={1}>
          <TableOptions
            toggleOptions={toggleOptions}
            activeToggle={statusFilter}
            onToggleChange={(v: string) => {
              setStatusFilter(v);
              setCurrentPage(1);
            }}
            searchValue={searchValue}
            onSearchChange={(v) => {
              setSearchValue(v);
              setCurrentPage(1);
            }}
            primaryAction={{
              label: bulkAccountMutation.isPending ? 'Accounting...' : 'Account All',
              onClick: handleBulkAccount,
            }}
          />
        </Box>
        {canManageStudents && (
          <Button
            visual="primary"
            size="sm"
            onClick={() => setAddDialogOpen(true)}
            data-e2e="event-student-add-button"
          >
            Add student
          </Button>
        )}
      </Flex>

      <Box pb={3}>
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : error ? (
          <Center py={12}>
            <Text color="text.danger">
              {error instanceof Error ? error.message : 'Failed to load students'}
            </Text>
          </Center>
        ) : students.length === 0 ? (
          <Center py={12}>
            {isFetching ? (
              <Spinner size="lg" color="button.primary" />
            ) : (
              <Text color="text.secondary">{t('events.noStudentsFound', 'No students found')}</Text>
            )}
          </Center>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={students}
              keyExtractor={(row) => row.id}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={handleRowClick}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </Box>

      <StudentDetailDrawer
        isOpen={!!selectedStudent}
        onClose={() => setSelectedStudent(undefined)}
        eventId={eventId}
        student={selectedStudent}
      />

      <AddEventStudentDialog
        isOpen={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        eventId={eventId}
        existingRosterPersonIds={existingRosterPersonIds}
      />
    </>
  );
}
